/**
 * AffiliateReturnHandler — runs once on dashboard load. If the URL carries
 * ?affiliate_session=<id>, polls /api/affiliate/checkout-status until either
 * is_affiliate flips to true (success), Stripe reports the session expired,
 * or we hit the polling cap. Then cleans up the URL.
 *
 * Per playbook §8: poll up to 5 times at 2s intervals.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX_POLLS = 5;
const POLL_INTERVAL_MS = 2000;

export default function AffiliateReturnHandler() {
  const loc = useLocation();
  const nav = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("affiliate_session");
    const cancelled = params.get("affiliate_cancelled");

    if (cancelled) {
      toast("Affiliate checkout cancelled — no charge made.", { icon: "↩️" });
      params.delete("affiliate_cancelled");
      nav({ pathname: loc.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
      return;
    }
    if (!sessionId || handled.current) return;
    handled.current = true;

    let cancel = false;
    let attempt = 0;
    const tick = async () => {
      if (cancel) return;
      attempt += 1;
      try {
        const r = await api.get(`/affiliate/checkout-status/${encodeURIComponent(sessionId)}`);
        const d = r.data;
        if (d.is_affiliate_now || d.txn_status === "completed" || d.live_payment_status === "paid") {
          toast.success("🎯 Welcome to the Affiliate program! Your gate is paid and your account is upgraded.");
          // Clean up URL
          params.delete("affiliate_session");
          nav({ pathname: loc.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
          // Soft-refresh user object so the rest of the dashboard renders affiliate state
          try { await api.get("/auth/me"); } catch { /* swallow */ }
          window.location.reload();
          return;
        }
        if (d.live_session_status === "expired") {
          toast.error("Affiliate checkout session expired. Please try again.");
          params.delete("affiliate_session");
          nav({ pathname: loc.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
          return;
        }
        if (attempt >= MAX_POLLS) {
          toast("Stripe is still processing your payment. We'll email you when your Affiliate tier is active.", { icon: "⏳" });
          params.delete("affiliate_session");
          nav({ pathname: loc.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempt >= MAX_POLLS) {
          toast.error(e.response?.data?.detail || "Could not verify Affiliate payment. Refresh and try again.");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();

    return () => { cancel = true; };
  }, [loc.search, loc.pathname, nav]);

  return null;
}
